import { memo, useEffect, useRef, useState, type ReactNode } from "react";
import type { DialVM } from "../dialModel";
import { DialView } from "./DialView";
import { Icon } from "./Icon";
import { balanceRows, tilesPerRow } from "../lib/tileRows";
import { tileMarkerFor } from "./tileMarker";

/**
 * A dial on My Dials, stripped to what the ticket asks for (KEY-516): the dial image, the instance
 * name beneath it, and when it last changed. Nothing else — no organisation, definition, completion
 * line, status bar or member stack. That is what makes six-across possible: the full {@link DialCard}
 * needs ~330px, this needs ~150px.
 *
 * The dropped organisation and definition names are kept on `title`/`aria-label` so the tile is still
 * identifiable on hover and to a screen reader, and so search results (which still match on both)
 * aren't completely opaque.
 *
 * KEY-434's "Needs setting up" badge survives as a corner badge over the image rather than a fourth
 * line: without it a dial that is waiting for its first-run details reads as one that is still
 * loading, which is the exact bug KEY-434 closed.
 */
export const DialTile = memo(function DialTile({
  vm,
  name,
  org,
  definitionName,
  updated,
  needsSetup = false,
  group = false,
  testing = false,
  demo = false,
  onOpen,
}: {
  vm: DialVM | null;
  /** The instance name — the tile's caption, and the only text on its face. */
  name: string;
  org: string;
  definitionName: string;
  /** Relative time of the dial's most recent status change (see `latestUpdate`). */
  updated: string;
  needsSetup?: boolean;
  /** True for a group dial — its action points are child dials (KEY-520). Outlines the tile blue. */
  group?: boolean;
  /** True when this dial only runs a version out for checking (KEY-283). Outlines the tile red. */
  testing?: boolean;
  /** True for a demo dial (KEY-938). Outlines the tile blue. */
  demo?: boolean;
  onOpen: () => void;
}) {
  const subtitle = [org, definitionName].filter(Boolean).join(" · ");
  const marker = tileMarkerFor({ group, testing, demo });
  return (
    <button
      onClick={onOpen}
      className="kd-card kd-card-hover kd-focusable"
      title={[subtitle || name, marker?.note].filter(Boolean).join(" — ")}
      aria-label={subtitle ? `${name} — ${subtitle}` : name}
      style={{
        padding: "var(--pad-tight)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 9,
        font: "inherit",
        color: "inherit",
        textAlign: "center",
        width: "100%",
        ...(marker ? { borderColor: marker.colour, borderWidth: marker.width } : {}),
      }}
    >
      {/* 1. The dial image. Square and full width so the ring fills the tile; the skeleton keeps the
          same box, so nothing reflows when the detail lands. */}
      <div style={{ position: "relative", width: "100%", aspectRatio: "1 / 1" }}>
        {vm ? (
          <DialView vm={vm} interactive={false} showLabels={false} />
        ) : (
          <div className="kd-skeleton" style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
        )}
        {needsSetup && (
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              overflow: "hidden",
              // Dark on amber: the status hues are fixed across themes, and white on this one is
              // barely legible. Matches DialCard's badge so the two pages agree.
              color: "#3a2a00",
              background: "var(--st-progress)",
              borderRadius: 999,
              padding: "1px 6px",
            }}
            title="This dial needs a few details before it can be opened."
          >
            Needs setting up
          </span>
        )}
      </div>

      {/* 2. The instance name, centred under the image. */}
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          lineHeight: 1.25,
          color: "var(--text)",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {name}
      </div>

      {/* KEY-603: a group of schools' dials are all named for the definition ("Ofsted dial"), so the name alone
          cannot tell them apart and the school used to appear only in the hover title. Shown whenever it adds
          something, i.e. when the dial's own name is not already the organisation's. */}
      {org && org !== name && (
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1.25,
            color: "var(--text-muted)",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            marginTop: -5,
          }}
        >
          {org}
        </div>
      )}

      {/* 3. When it last changed. */}
      {updated && (
        <div
          style={{
            fontSize: 11.5,
            fontWeight: 500,
            color: "var(--text-faint)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <Icon name="clock" size={11} />
          {updated}
        </div>
      )}
    </button>
  );
},
(prev, next) =>
  prev.vm === next.vm &&
  prev.name === next.name &&
  prev.org === next.org &&
  prev.definitionName === next.definitionName &&
  prev.updated === next.updated &&
  prev.needsSetup === next.needsSetup);

/**
 * Measures a container and reports how many tiles fit across it, capped at six.
 *
 * Seeded from the window so the first paint is already about right, then corrected by a
 * `ResizeObserver` on the element itself. Falls back to the seed where `ResizeObserver` doesn't exist
 * (a non-DOM test environment) rather than throwing.
 */
export function useTilesPerRow(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [perRow, setPerRow] = useState(() =>
    // The page is a 1180px column with 28px of padding either side; the window is the limit below that.
    tilesPerRow(typeof window === "undefined" ? 1124 : Math.min(window.innerWidth, 1180) - 56),
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const next = tilesPerRow(el.clientWidth);
      setPerRow((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, perRow];
}

/**
 * Lays `items` out in balanced rows of at most `perRow` (see `balanceRowLengths`) — one flex row per
 * computed length, so a short final row centres instead of hugging the left edge. `perRow` is passed
 * in rather than measured here so every section on the page shares one tile width.
 */
export function TileRows<T>({
  items,
  perRow,
  keyOf,
  children,
  style,
}: {
  items: readonly T[];
  perRow: number;
  keyOf: (item: T) => string;
  children: (item: T) => ReactNode;
  style?: React.CSSProperties;
}) {
  const rows = balanceRows(items, perRow);
  return (
    <div className="kd-tiles" style={{ ["--kd-tile-cols" as string]: perRow, ...style }}>
      {rows.map((row) => (
        <div className="kd-tile-row" key={keyOf(row[0])}>
          {row.map((item) => (
            <div key={keyOf(item)}>{children(item)}</div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * What My Dials shows while the first wave of requests is in flight (KEY-238) — placeholders sharing
 * the tile's shape and the row geometry, so the real tiles replace them in place rather than shifting
 * the page. The count is a guess; it only has to look like "dials are coming".
 */
export function DialTileSkeletons({ count = 6 }: { count?: number }) {
  const [ref, perRow] = useTilesPerRow();
  const ids = Array.from({ length: count }, (_, i) => String(i));
  return (
    <div ref={ref} aria-busy="true" aria-label="Loading your dials">
      <TileRows items={ids} perRow={perRow} keyOf={(id) => id}>
        {() => (
          <div
            className="kd-card"
            style={{ padding: "var(--pad-tight)", display: "flex", flexDirection: "column", gap: 9 }}
          >
            <div className="kd-skeleton" style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: "50%" }} />
            <div className="kd-skeleton" style={{ height: 14, width: "70%", borderRadius: 5, margin: "0 auto" }} />
            <div className="kd-skeleton" style={{ height: 10, width: "45%", borderRadius: 5, margin: "0 auto" }} />
          </div>
        )}
      </TileRows>
    </div>
  );
}
